import { MediaImage } from "@/components/atoms/media";
import {
  Box,
  chakra,
  Divider,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { PortfolioItemData } from "../portfolioItem";
import DOMPurify from "isomorphic-dompurify";
import { useTranslations } from "next-intl";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import styles from "./portfolioItemModal.module.scss";

export interface PortfolioItemModalProps {
  item: PortfolioItemData;
  isOpen: boolean;
  onClose: () => void;
}

export default chakra(PortfolioItemModal);
function PortfolioItemModal({
  item,
  isOpen,
  onClose,
}: PortfolioItemModalProps) {
  const t = useTranslations();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay display={{ base: "none", md: "block" }} />

      <ModalContent
        overflowY="auto"
        minW={{ base: "full", lg: "80%", xl: "70%" }}
        minH={{ base: "95vh", md: "fit-content" }}
        maxH={{ base: "95vh", md: "none" }}
        mt={{ base: "5vh", md: 0 }}
        mb={0}
        borderRadius="none"
        borderTopRadius={{ base: "1rem", md: "none" }}
        boxShadow={{
          base: "0 -4px 34px 0 rgba(0, 0, 0, 0.15)",
          md: "none",
        }}
      >
        {/* Desktop close button and header */}
        <Box
          display={{ base: "none", md: "flex" }}
          justifyContent="flex-end"
          m={5}
          mb={2}
        >
          <ModalCloseButton
            position="static"
            size="lg"
            color="black"
            _hover={{ bgColor: "transparent" }}
            _active={{ bgColor: "transparent" }}
          />
        </Box>
        {item.title && (
          <ModalHeader
            display={{ base: "none", md: "block" }}
            px={10}
            py={0}
            textStyle="display.lg"
            fontSize="2.75rem"
            fontWeight={400}
            color="brand.aubergine"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                item.title.replace(/<\/p>/g, "</br>"),
                {
                  ALLOWED_TAGS: ["strong", "br"],
                },
              ),
            }}
            sx={{
              strong: {
                fontWeight: 300,
                color: "brand.cyan",
                fontFamily: "Lausanne, sans-serif",
              },
            }}
          />
        )}

        {/* Mobile close button and header */}
        <Box
          display={{ base: "block", md: "none" }}
          position="sticky"
          top={0}
          zIndex={2}
          bg="white"
        >
          {item.title && (
            <ModalHeader
              px={4}
              textStyle="label.md"
              fontWeight={400}
              color="brand.cyan"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.title, {
                  ALLOWED_TAGS: ["strong", "br"],
                }),
              }}
              sx={{
                strong: {
                  display: "block",
                  textStyle: "display.xs",
                  color: "brand.aubergine",
                  fontWeight: 400,
                },
              }}
            />
          )}

          <ModalCloseButton
            position="absolute"
            top={3}
            right={3}
            size="lg"
            color="brand.aubergine"
            _hover={{ bgColor: "transparent" }}
            _active={{ bgColor: "transparent" }}
          />

          <Divider borderColor="aubergine.200" />
        </Box>

        <ModalBody px={{ base: 4, md: 10 }} pt={5} pb={10}>
          <Box display="flex" gap={10}>
            {item.mediaItem && (
              <Box display={{ base: "none", md: "block" }} flex="0 0 45%">
                <MediaImage
                  {...item.mediaItem}
                  viewportWidthFactor={{ base: 1 }}
                  cropSizeAlias={{ base: "square" }}
                  hasBorderRadius={true}
                  imageUrlBuilder={buildImageUrl}
                  aspectRatio="5 / 4"
                />
              </Box>
            )}
            {item.introduction && (
              <Text
                textStyle="paragraph.lg"
                color="gray.800"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item.introduction, {
                    ALLOWED_TAGS: ["p", "strong", "br"],
                  }),
                }}
              />
            )}
          </Box>
          <Grid
            className={styles.detailsGrid}
            templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
            rowGap={8}
            w="100%"
            my={8}
          >
            {item.type && (
              <Box>
                <Text mb={1} textStyle="label.md">
                  {t("type")}
                </Text>
                <Text textStyle="heading.xs">{item.type}</Text>
              </Box>
            )}
            {item.location && (
              <Box>
                <Text mb={1} textStyle="label.md">
                  {t("location")}
                </Text>
                <Text textStyle="heading.xs">{item.location}</Text>
              </Box>
            )}
            {item.surface && (
              <Box>
                <Text mb={1} textStyle="label.md">
                  {t("rentableFloorArea")}
                </Text>
                <Text textStyle="heading.xs">{item.surface}</Text>
              </Box>
            )}
            {item.status && (
              <Box>
                <Text mb={1} textStyle="label.md">
                  {t("status")}
                </Text>
                <Text textStyle="heading.xs">{item.status}</Text>
              </Box>
            )}
          </Grid>
          {item.mediaItem && (
            <MediaImage
              {...item.mediaItem}
              viewportWidthFactor={{ base: 1 }}
              cropSizeAlias={{ base: "square" }}
              hasBorderRadius={true}
              imageUrlBuilder={buildImageUrl}
              display={{ base: "block", md: "none" }}
              h="17.875rem"
              aspectRatio="5 / 4"
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
